/* eslint-disable @typescript-eslint/naming-convention */
import {
  ReactNode,
  SyntheticEvent,
  ReactText,
  Dispatch,
  SetStateAction,
  UIEvent,
} from 'react';

type SortOrder = 'ascending' | 'descending' | undefined;

interface SortObject {
  sortOrder?: SortOrder;
  activeSortKey: string | undefined;
}

// type RowClick<T> = (
//   event: SyntheticEvent<Element, Event>,
//   record: T,
//   rowIndex: string | number
// ) => void;

type RowClick<T> = ({
  event,
  rowData,
  rowIndex,
  onExpandClick,
  rowElement,
}: {
  event: SyntheticEvent<Element, Event>;
  rowData: T;
  rowIndex: string | number;
  onExpandClick: () => void;
  rowElement: HTMLTableRowElement;
}) => void;

enum Align {
  left = 'left',
  center = 'center',
  right = 'right',
}

/**
 * @param renderView - render a custom view for the cell
 * @param render - same as renderView, but uses named arguments instead of object
 * @param columnFooterComponent - render a custom footer for each column (showFooter should be true in Table)
 * @param allowAnyRowExpandCollapse - allow any row to be expanded or collapsed even if any cell is expanded
 * @param key - unique key for the row
 * @param dataIndex - key to identify column
 * @param expandable - if true, the row will be expandable - (requires expandableRowsComponent in Table)
 * @title - title to display in the header
 * @titleWithAccessor - same as title, but uses a function instead of a string. Use Either title or titleWithAccessor, not both
 */
interface Columns<T> {
  key: string;
  dataIndex: string;
  title?: string | ReactNode;
  titleWithAccessor?: (tableData: T[]) => ReactNode;
  width?: number | 'auto';
  minWidth?: number;
  bodyCellClassName?: string;
  headerCellClassName?: string;
  sortable?: boolean;
  hide?: boolean;
  fixed?: boolean;
  align?: Align;
  allowAnyRowExpandCollapse?: boolean;
  columnFooterComponent?: (tableData: T[]) => ReactNode;
  footerCellClass?: string;
  renderView?: ({
    displayValue,
    rowData,
    rowIndex,
    onExpandClick,
    isExpanded,
    dataIndex,
    colIndex,
  }: {
    displayValue: ReactText;
    rowData: T;
    rowIndex: number;
    onExpandClick: () => void;
    colIndex: number;
    isExpanded: boolean;
    dataIndex: string;
  }) => ReactNode;
  render?: (
    displayValue: ReactText,
    rowData: T,
    onExpandClick: () => void,
    isExpanded: boolean,
    dataIndex: string,
    rowIndex: number,
    colIndex: number
  ) => ReactNode;
  expandable?: boolean;
  disableRowClick?: boolean;
  columnResize?: boolean;
  label?: string; // to be used by DeltaDnd Component
  testId?: string;
  sub?: Array<Record<string, any>>;
}

interface TableProps<T> {
  data: T[];
  columns: Array<Columns<T>>;
  loading?: boolean;
  fixedHeader?: boolean;
  emptyComponent?: ReactNode;
  responsive?: boolean;
  tableContainerClass?: string;
  bodyRowClass?: string;
  emptyTableClass?: string;
  expandedRowGlobalClass?: string;
  headerRowClass?: string;
  footerRowClass?: string;
  className?: string;
  sortDirections?: SortOrder[];
  height?: string | number;
  width?: string | number;
  rowHeight?: number;
  minWidth?: string | number;
  showFooter?: boolean;
  expandableRowsComponent?: ({
    expandedRowData,
    expandedRowIndexName,
    handleResetExpansion,
  }: {
    expandedRowData: T;
    expandedRowIndexName: Columns<T>['dataIndex'];
    handleResetExpansion: () => void;
  }) => JSX.Element;
  onSortChange?: (sortObject: SortObject) => void;
  onRowClick?: RowClick<T> | undefined;
  onRowMouseEnter?: RowClick<T>;
  onRowMouseLeave?: RowClick<T>;
  allowAccordionExpand?: boolean;
  autoSizeAllColumns?: boolean;
  resizeColumnsToFit?: boolean;
  columnResize?: boolean;
  onScroll?: (e: UIEvent<HTMLDivElement>) => void;
  virtualOverscan?: number;
  positionAbsolute?: boolean;
  'data-testid'?: string;
  showBody?: boolean;
  autoScroll?: {
    reverseScroll: boolean;
    debounced: boolean;
    visibilityChange: boolean;
    scrollOnChange?: string;
  };
  tbodyStartElement?: JSX.Element;
  tBodyEndElement?: JSX.Element;
}

interface TableRowsProps<T> {
  // data: Array<Record<string, any>>;
  // columns: Array<Columns<Record<string, any>>>;
  rowClass?: string;
  expandedRowGlobalClass?: string;
  sortOrder?: SortOrder;
  showFooter?: boolean;
  footerRowClass?: string;
  expandableRowsComponent: ({
    expandedRowData,
    expandedRowIndexName,
    handleResetExpansion,
  }: {
    expandedRowData: T;
    expandedRowIndexName: Columns<T>['dataIndex'];
    handleResetExpansion: () => void;
  }) => JSX.Element;
  onRowClick?: RowClick<T> | undefined;
  onRowMouseEnter?: RowClick<T>;
  onRowMouseLeave?: RowClick<T>;
  allowAccordionExpand?: boolean;
  parentRef: { current: HTMLDivElement | null };
  virtualOverscan?: number;
  rowHeight: number | undefined;
  showBody?: boolean;
  autoScroll: {
    reverseScroll: boolean;
    debounced: boolean;
    visibilityChange: boolean;
    scrollOnChange?: string;
  };
}

interface TableHeaderProps<T> {
  // columns?: Array<Columns<T>>;
  data?: T[];
  rowClass?: string;
  sortDirections?: SortOrder[];
  fixedHeader?: boolean;
  // sortOrder?: SortOrder;
  setSortOrder?: Dispatch<SetStateAction<SortOrder>>;
  onSortChange?: (sortObject: SortObject) => void;
  columnResize?: boolean;
}

export type {
  SortOrder,
  SortObject,
  Columns,
  TableProps,
  TableRowsProps,
  TableHeaderProps,
  RowClick,
};

export { Align };
